import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import NoDataTileBody from 'commons/src/components/tile/NoDataTileBody';
import { sensorUnits } from 'commons/src/constants';
import { getWeatherIcon, icons } from 'commons/src/features/outdoorAirQualityPage/WeatherAttrs';
import IconPM from 'commons/src/img/sensorIcons/IconPM';
import { SensorTypes } from 'commons/src/models/commonEnums';
import { Pollutants, Weather } from 'commons/src/models/commonTypeScript';
import calcTextSize, { getParentSize } from './commonFunctions';
import PublicDashboardTileHeader from './TileHeader';

export type Props = {
    currentWeather: Weather;
    currentPollutants: Pollutants;
    outdoorRef: { name: string; lat: number; lng: number };
    id: string;
    numberOfTilesInWidth: number;
};

export const PublicDashboardOutdoorTileComponent = (props: Props): React.ReactElement => {
    const {
        currentWeather: weather,
        outdoorRef,
        currentPollutants: pollutantsObject,
        numberOfTilesInWidth,
        id,
    } = props;
    const { t: txt } = useTranslation();
    const [sensorValueFontSize, setSensorValueFontSize] = useState('60px');
    const [sensorNameFontSize, setSensorNameFontSize] = useState('16px');
    const [currentNumberOfTiles, setCurrentNumberOfTiles] = useState(numberOfTilesInWidth);

    const weatherError = !weather.iconCode;
    const pollutantsError = !pollutantsObject || Object.keys(pollutantsObject.pollutants).length === 0;
    const errorMessage = 'AirQualityDataUnavailable';

    const calcText = (text: HTMLElement, updateFont: (font: string) => void): void => {
        const { parentWidth, parentHeight, textHeight } = getParentSize(text);
        let parentContainerHeight = parentHeight * 0.8;
        if (currentNumberOfTiles && numberOfTilesInWidth > currentNumberOfTiles) {
            parentContainerHeight *= currentNumberOfTiles / numberOfTilesInWidth;
        }
        const maxTextGrowth = parentContainerHeight / textHeight;
        calcTextSize(text, parentWidth, updateFont, maxTextGrowth);
    };

    const getWidestText = (text1: HTMLElement | null, text2: HTMLElement | null): null | HTMLElement => {
        if (text1 && text2) {
            const currentText1Width = text1 && text1.scrollWidth;
            const currentText2Width = text2 && text2.scrollWidth;
            return currentText1Width > currentText2Width ? text1 : text2;
        }
        return text1 || text2;
    };
    const calculateTextSizes = () => {
        const sensorValueTemp = document.getElementById(`${id}-temp-value`);
        const sensorValueIaq = document.getElementById(`${id}-iaq-value`);
        const sensorNameTemp = document.getElementById(`${id}-unit`);
        const sensorNameIaq = document.getElementById(`${id}-iaq`);
        const widestSensorName = getWidestText(sensorNameIaq, sensorNameTemp);
        const widestSensorValue = getWidestText(sensorValueTemp, sensorValueIaq);
        if (widestSensorValue) {
            calcText(widestSensorValue, setSensorValueFontSize);
        }
        if (widestSensorName) {
            calcText(widestSensorName, setSensorNameFontSize);
        }
    };

    useEffect((): void => {
        calculateTextSizes();
        setCurrentNumberOfTiles(numberOfTilesInWidth);
    }, [numberOfTilesInWidth]);

    useEffect(() => {
        window.addEventListener('resize', calculateTextSizes);
        return (): void => window.removeEventListener('resize', calculateTextSizes);
    }, []);

    const pmValues = pollutantsObject.pollutants.pm25;
    const pmString = pmValues ? `${txt(SensorTypes.pm25).toUpperCase()}${`(${sensorUnits[pmValues.unit]})`}` : '';
    const temperatureString = !weatherError
        ? `${weather.sensors?.temperature.value.toFixed(0)}\u00B0`
        : txt('NotAvailableAbbreviation');
    const weatherUnitString =
        weather && weather.sensors?.temperature && weather.sensors?.temperature.unit === 'FAHRENHEIT'
            ? txt('Units.UnitFahrenheit')
            : txt('Units.UnitCelsius');
    return (
        <div className="tile-wrapper">
            <PublicDashboardTileHeader
                headerText={txt('OutdoorAir')}
                subHeaderText={outdoorRef.name}
                id={id}
                numberOfTilesInWidth={numberOfTilesInWidth}
            />
            <div className="tile-body">
                {!pollutantsError ? (
                    <div className="public-dashboard-tile__outdoor">
                        <div className="public-dashboard-tile__outdoor__grid public-dashboard-tile__outdoor__grid--top">
                            <div className="public-dashboard-tile__outdoor__grid-item">
                                {!weatherError && getWeatherIcon(weather.iconCode as keyof typeof icons)}
                            </div>
                            <div className="public-dashboard-tile__outdoor__grid-item">
                                <IconPM />
                            </div>
                        </div>
                        <div className="public-dashboard-tile__outdoor__grid public-dashboard-tile__outdoor__grid--top">
                            <div className="public-dashboard-tile__outdoor__grid-item text-bold">
                                <div id={`${id}-temp-value`} style={{ fontSize: sensorValueFontSize }}>
                                    {temperatureString}
                                </div>
                            </div>
                            <div className="public-dashboard-tile__outdoor__grid-item text-bold">
                                <div id={`${id}-iaq-value`} style={{ fontSize: sensorValueFontSize }}>
                                    {pmValues?.value || txt('NotAvailableAbbreviation')}
                                </div>
                            </div>
                        </div>
                        <div className="public-dashboard-tile__outdoor__grid public-dashboard-tile__outdoor__grid--bottom">
                            <div className="public-dashboard-tile__outdoor__grid-item">
                                <div id={`${id}-unit`} style={{ fontSize: sensorNameFontSize }}>
                                    {weatherUnitString}
                                </div>
                            </div>
                            <div className="public-dashboard-tile__outdoor__grid-item">
                                <div id={`${id}-pm25`} style={{ fontSize: sensorNameFontSize }}>
                                    {pmString}
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <NoDataTileBody message={errorMessage} />
                )}
            </div>
        </div>
    );
};

export default PublicDashboardOutdoorTileComponent;
